import React, { useState } from 'react';
import { PublicLayout } from '../../components/layout';
import {
  ContactRequestCategory,
  ContactRequestPriority,
  submitContactRequest,
} from '../../services/supportService';

interface ContactFormData {
  name: string;
  email: string;
  phone: string;
  subject: string;
  message: string;
  priority: ContactRequestPriority;
  category: ContactRequestCategory;
}

interface Office {
  city: string;
  address: string;
  phone?: string;
  email: string;
  hours: string;
}

const emptyFormData: ContactFormData = {
  name: '',
  email: '',
  phone: '',
  subject: '',
  message: '',
  priority: 'medium',
  category: 'general',
};

function getSubmitErrorMessage(error: unknown): string {
  const code =
    typeof error === 'object' && error && 'code' in error
      ? String((error as { code?: string }).code)
      : '';

  if (code === 'functions/resource-exhausted') {
    return 'تم إرسال طلب قريباً من هذا البريد. انتظر دقيقة ثم حاول مرة أخرى.';
  }

  if (code === 'functions/invalid-argument') {
    return 'راجع البيانات المدخلة وتأكد من اكتمال الحقول المطلوبة.';
  }

  return 'تعذر إرسال الرسالة الآن. حاول مرة أخرى بعد قليل.';
}

const ContactUs: React.FC = () => {
  const [formData, setFormData] = useState<ContactFormData>(emptyFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [ticketId, setTicketId] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError('');

    try {
      const result = await submitContactRequest(formData);
      setTicketId(result.ticketId);
      setSubmitted(true);
    } catch (error) {
      setSubmitError(getSubmitErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setSubmitError('');
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const contactMethods = [
    {
      icon: '📞',
      title: 'الهاتف',
      info: '+20 11 531 14266',
      description: 'متاح من الأحد إلى الخميس، 9 صباحاً - 6 مساءً',
      action: 'اتصال الآن',
      href: 'tel:+201153114266',
    },
    {
      icon: '💬',
      title: 'الدردشة المباشرة',
      info: 'متاح الآن',
      description: 'احصل على مساعدة فورية من فريق الدعم',
      action: 'إرسال طلب',
      href: '#contact-form',
    },
    {
      icon: '📧',
      title: 'البريد الإلكتروني',
      info: 'support@class-tech.net',
      description: 'سنرد على رسالتك خلال 24 ساعة',
      action: 'إرسال إيميل',
      href: 'mailto:support@class-tech.net',
    },
    {
      icon: '📍',
      title: 'العنوان',
      info: 'الجيزة، مصر',
      description: '22 ابو بكر الصديق - ابن حمد - المساكن - بولاق الدكرور - الجيزة',
      action: 'عرض الخريطة',
      href: 'https://www.google.com/maps/search/?api=1&query=22%20Abu%20Bakr%20as%20Siddiq%20-%20Ibn%20Hammam%20-%20Al-Masakinah%20-%20Al-Haram%20Giza%20Egypt',
    },
  ];

  const offices: Office[] = [
    {
      city: 'مصر',
      address: '22 ابو بكر الصديق - ابن حمد - المساكن - بولاق الدكرور - الجيزة',
      email: 'info@class-tech.net',
      hours: 'الأحد - الخميس: 9 صباحاً - 6 مساءً',
    },
  ];

  if (submitted) {
    return (
      <PublicLayout>
        <div className='min-h-[70vh] bg-gradient-to-br from-primary-50 to-secondary-50 flex items-center justify-center px-4 py-16'>
          <div className='max-w-md mx-auto text-center bg-white rounded-2xl shadow-lg p-8'>
            <div className='text-6xl mb-6'>✅</div>
            <h2 className='text-2xl font-bold text-secondary-900 mb-4'>
              تم إرسال رسالتك بنجاح!
            </h2>
            <p className='text-secondary-600 mb-3'>
              شكراً لتواصلك معنا. سيقوم فريقنا بالرد عليك خلال 24 ساعة.
            </p>
            {ticketId ? (
              <p className='text-sm text-secondary-500 mb-6'>
                رقم الطلب: <span dir='ltr'>{ticketId}</span>
              </p>
            ) : null}
            <button
              type='button'
              onClick={() => {
                setSubmitted(false);
                setTicketId('');
                setSubmitError('');
                setFormData(emptyFormData);
              }}
              className='bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors'
            >
              إرسال رسالة أخرى
            </button>
          </div>
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <div className='bg-gradient-to-br from-primary-50 to-secondary-50'>
        {/* Hero Section */}
        <div className='bg-gradient-to-r from-primary-600 to-primary-800 text-white py-20'>
          <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
            <div className='text-center'>
              <h1 className='text-4xl md:text-6xl font-bold mb-6'>
                تواصل معنا
              </h1>
              <p className='text-xl md:text-2xl text-primary-100 max-w-3xl mx-auto'>
                نحن هنا لمساعدتك. تواصل معنا بالطريقة التي تناسبك
              </p>
            </div>
          </div>
        </div>

        {/* Contact Methods */}
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16'>
          <div className='text-center mb-12'>
            <h2 className='text-3xl font-bold text-secondary-900 mb-4'>
              طرق التواصل
            </h2>
            <p className='text-lg text-secondary-600'>
              اختر الطريقة الأنسب للتواصل مع فريق الدعم
            </p>
          </div>

          <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16'>
            {contactMethods.map((method, index) => (
              <div
                key={index}
                className='bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow p-6 text-center'
              >
                <span className='text-4xl mb-4 block'>{method.icon}</span>
                <h3 className='font-semibold text-secondary-900 mb-2'>
                  {method.title}
                </h3>
                <div className='font-medium text-primary-600 mb-2'>
                  {method.info}
                </div>
                <p className='text-sm text-secondary-600 mb-4'>
                  {method.description}
                </p>
                <a
                  href={method.href}
                  className='inline-flex bg-primary-100 hover:bg-primary-200 text-primary-700 px-4 py-2 rounded-lg text-sm font-medium transition-colors'
                >
                  {method.action}
                </a>
              </div>
            ))}
          </div>
        </div>

        {/* Contact Form and Info */}
        <div id='contact-form' className='bg-white py-16 scroll-mt-24'>
          <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
            <div className='grid grid-cols-1 lg:grid-cols-2 gap-12'>
              {/* Contact Form */}
              <div>
                <h2 className='text-3xl font-bold text-secondary-900 mb-6'>
                  أرسل لنا رسالة
                </h2>
                <form onSubmit={handleSubmit} className='space-y-6'>
                  <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                    <div>
                      <label
                        htmlFor='name'
                        className='block text-sm font-medium text-secondary-700 mb-2'
                      >
                        الاسم الكامل *
                      </label>
                      <input
                        type='text'
                        id='name'
                        name='name'
                        required
                        value={formData.name}
                        onChange={handleInputChange}
                        className='w-full px-4 py-3 border border-secondary-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent'
                        placeholder='أدخل اسمك الكامل'
                      />
                    </div>
                    <div>
                      <label
                        htmlFor='email'
                        className='block text-sm font-medium text-secondary-700 mb-2'
                      >
                        البريد الإلكتروني *
                      </label>
                      <input
                        type='email'
                        id='email'
                        name='email'
                        required
                        value={formData.email}
                        onChange={handleInputChange}
                        className='w-full px-4 py-3 border border-secondary-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent'
                        placeholder='example@email.com'
                      />
                    </div>
                  </div>

                  <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                    <div>
                      <label
                        htmlFor='phone'
                        className='block text-sm font-medium text-secondary-700 mb-2'
                      >
                        رقم الهاتف
                      </label>
                      <input
                        type='tel'
                        id='phone'
                        name='phone'
                        value={formData.phone}
                        onChange={handleInputChange}
                        className='w-full px-4 py-3 border border-secondary-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent'
                        placeholder='+966 5X XXX XXXX'
                      />
                    </div>
                    <div>
                      <label
                        htmlFor='category'
                        className='block text-sm font-medium text-secondary-700 mb-2'
                      >
                        فئة الاستفسار
                      </label>
                      <select
                        id='category'
                        name='category'
                        value={formData.category}
                        onChange={handleInputChange}
                        className='w-full px-4 py-3 border border-secondary-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent'
                      >
                        <option value='general'>استفسار عام</option>
                        <option value='technical'>مشكلة تقنية</option>
                        <option value='billing'>الفوترة والدفع</option>
                        <option value='features'>الميزات والوظائف</option>
                        <option value='account'>إدارة الحساب</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label
                      htmlFor='subject'
                      className='block text-sm font-medium text-secondary-700 mb-2'
                    >
                      موضوع الرسالة *
                    </label>
                    <input
                      type='text'
                      id='subject'
                      name='subject'
                      required
                      value={formData.subject}
                      onChange={handleInputChange}
                      className='w-full px-4 py-3 border border-secondary-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent'
                      placeholder='مثال: مشكلة في تسجيل الدخول'
                    />
                  </div>

                  <div>
                    <label
                      htmlFor='priority'
                      className='block text-sm font-medium text-secondary-700 mb-2'
                    >
                      أولوية الطلب
                    </label>
                    <select
                      id='priority'
                      name='priority'
                      value={formData.priority}
                      onChange={handleInputChange}
                      className='w-full px-4 py-3 border border-secondary-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent'
                    >
                      <option value='low'>منخفضة</option>
                      <option value='medium'>متوسطة</option>
                      <option value='high'>عالية</option>
                      <option value='urgent'>عاجلة</option>
                    </select>
                  </div>

                  <div>
                    <label
                      htmlFor='message'
                      className='block text-sm font-medium text-secondary-700 mb-2'
                    >
                      تفاصيل الرسالة *
                    </label>
                    <textarea
                      id='message'
                      name='message'
                      required
                      rows={6}
                      value={formData.message}
                      onChange={handleInputChange}
                      className='w-full px-4 py-3 border border-secondary-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none'
                      placeholder='اكتب تفاصيل استفسارك أو مشكلتك هنا...'
                    />
                  </div>

                  <button
                    type='submit'
                    disabled={isSubmitting}
                    className='w-full bg-primary-600 hover:bg-primary-700 disabled:bg-primary-400 text-white px-6 py-3 rounded-lg font-semibold transition-colors flex items-center justify-center'
                  >
                    {isSubmitting ? (
                      <>
                        <div className='animate-spin rounded-full h-5 w-5 border-b-2 border-white ml-2'></div>
                        جاري الإرسال...
                      </>
                    ) : (
                      'إرسال الرسالة'
                    )}
                  </button>
                  {submitError ? (
                    <div
                      role='alert'
                      className='rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700'
                    >
                      {submitError}
                    </div>
                  ) : null}
                </form>
              </div>

              {/* Office Information */}
              <div>
                <h2 className='text-3xl font-bold text-secondary-900 mb-6'>
                  مكاتبنا
                </h2>
                <div className='space-y-6'>
                  {offices.map((office, index) => (
                    <div key={index} className='bg-secondary-50 rounded-lg p-6'>
                      <h3 className='font-semibold text-secondary-900 text-lg mb-3'>
                        📍 {office.city}
                      </h3>
                      <div className='space-y-2 text-secondary-700'>
                        <p>
                          <span className='font-medium'>العنوان:</span>{' '}
                          {office.address}
                        </p>
                        {office.phone ? (
                          <p>
                            <span className='font-medium'>الهاتف:</span>{' '}
                            {office.phone}
                          </p>
                        ) : null}
                        <p>
                          <span className='font-medium'>البريد:</span>{' '}
                          {office.email}
                        </p>
                        <p>
                          <span className='font-medium'>ساعات العمل:</span>{' '}
                          {office.hours}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Additional Info */}
                <div className='mt-8 bg-primary-50 rounded-lg p-6'>
                  <h3 className='font-semibold text-primary-900 mb-3'>
                    💡 نصائح للحصول على رد سريع
                  </h3>
                  <ul className='space-y-2 text-sm text-primary-800'>
                    <li>• كن محدداً في وصف المشكلة</li>
                    <li>• أرفق لقطات شاشة إذا أمكن</li>
                    <li>• اذكر نوع المتصفح ونظام التشغيل</li>
                    <li>• قدم خطوات إعادة إنتاج المشكلة</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PublicLayout>
  );
};

export default ContactUs;
